import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const QPCREfficiencyCalculator = () => {
  const [qpcrData, setQpcrData] = useState({
    cycleNumbers: '',
    ctValues: '',
    templateConcentrations: '',
    amplificationEfficiency: '',
    rSquared: '',
    slope: '',
    intercept: '',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQpcrData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setQpcrData(prev => ({
      ...prev,
      [name]: checked
    }));
    setError('');
  };

  const calculateQPCREfficiency = () => {
    const { cycleNumbers, ctValues, templateConcentrations, slope, intercept } = qpcrData;

    if (!cycleNumbers || !ctValues || !templateConcentrations) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const cycles = cycleNumbers.split(',').map(Number);
      const cts = ctValues.split(',').map(Number);
      const concentrations = templateConcentrations.split(',').map(Number);

      if (cycles.length !== cts.length || cts.length !== concentrations.length) {
        setError('All input arrays must have the same length');
        return;
      }

      const calculateEfficiency = () => {
        const calculatedSlope = slope || calculateSlope(cycles, cts);
        const efficiency = Math.pow(10, (-1 / calculatedSlope)) - 1;
        const efficiencyPercentage = efficiency * 100;
        const rSquared = calculateRSquared(cycles, cts, calculatedSlope, intercept);
        const theoreticalCts = cycles.map(cycle => intercept + (cycle * calculatedSlope));
        const stdDev = calculateStandardDeviation(cts, theoreticalCts);

        return {
          efficiency: efficiency.toFixed(3),
          efficiencyPercentage: efficiencyPercentage.toFixed(1),
          rSquared: rSquared.toFixed(3),
          stdDev: stdDev.toFixed(3),
          theoreticalCts
        };
      };

      const generateChartData = (results) => {
        const labels = cycles;
        const theoreticalData = results.theoreticalCts;

        return {
          labels,
          datasets: [
            {
              label: 'Actual CT Values',
              data: cts,
              borderColor: 'rgb(75, 192, 192)',
              tension: 0.1,
              fill: false
            },
            {
              label: 'Theoretical CT Values',
              data: theoreticalData,
              borderColor: 'rgb(255, 99, 132)',
              tension: 0.1,
              fill: false,
              borderDash: [5, 5]
            }
          ]
        };
      };

      const resultsData = {
        cycleNumbers,
        ctValues,
        templateConcentrations,
        slope,
        intercept,
        ...calculateEfficiency()
      };

      setResults(resultsData);
      setChartData(generateChartData(resultsData));

    } catch (error) {
      setError('Please enter valid numbers for all fields');
    }
  };

  const calculateSlope = (cycles, cts) => {
    const n = cycles.length;
    const sumX = cycles.reduce((acc, x) => acc + x, 0);
    const sumY = cts.reduce((acc, y) => acc + y, 0);
    const sumXY = cycles.reduce((acc, x, i) => acc + (x * cts[i]), 0);
    const sumX2 = cycles.reduce((acc, x) => acc + (x * x), 0);

    return (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  };

  const calculateRSquared = (cycles, cts, slope, intercept) => {
    const predicted = cycles.map(cycle => intercept + (cycle * slope));
    const meanY = cts.reduce((acc, y) => acc + y, 0) / cts.length;
    
    const ssTotal = cts.reduce((acc, y) => acc + Math.pow(y - meanY, 2), 0);
    const ssResidual = cts.reduce((acc, y, i) => acc + Math.pow(y - predicted[i], 2), 0);

    return 1 - (ssResidual / ssTotal);
  };

  const calculateStandardDeviation = (actual, theoretical) => {
    const squaredDiffs = actual.map((val, i) => Math.pow(val - theoretical[i], 2));
    return Math.sqrt(squaredDiffs.reduce((acc, val) => acc + val, 0) / squaredDiffs.length);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>qPCR Efficiency Calculator | Advanced PCR Analysis Tool</title>
        <meta name="description" content="Calculate qPCR amplification efficiency with our advanced calculator. Analyze CT values, template concentrations, and statistical metrics for accurate PCR efficiency assessment." />
        <meta name="keywords" content="qPCR calculator, PCR efficiency, CT value analysis, molecular biology tools, PCR efficiency calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">🔬</div>
            <h1 className="text-3xl font-bold text-gray-800">qPCR Efficiency Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The qPCR Efficiency Calculator helps you analyze PCR amplification efficiency and provides:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>✓ Amplification efficiency calculations</li>
              <li>✓ R-squared value analysis</li>
              <li>✓ Standard deviation assessment</li>
              <li>✓ Theoretical vs actual CT value comparison</li>
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Numbers (Cycles):</label>
                <input
                  type="text"
                  id="cycleNumbers"
                  name="cycleNumbers"
                  value={qpcrData.cycleNumbers}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter cycle numbers (comma-separated)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CT Values:</label>
                <input
                  type="text"
                  id="ctValues"
                  name="ctValues"
                  value={qpcrData.ctValues}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter CT values (comma-separated)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Concentrations:</label>
                <input
                  type="text"
                  id="templateConcentrations"
                  name="templateConcentrations"
                  value={qpcrData.templateConcentrations}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter template concentrations (comma-separated)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slope (optional):</label>
                <input
                  type="number"
                  id="slope"
                  name="slope"
                  value={qpcrData.slope}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter slope value (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Intercept (optional):</label>
                <input
                  type="number"
                  id="intercept"
                  name="intercept"
                  value={qpcrData.intercept}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter intercept value (optional)"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="showDetailedAnalysis"
                  checked={qpcrData.showDetailedAnalysis}
                  onChange={handleCheckboxChange}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Show detailed analysis and statistical breakdown
                </label>
              </div>
            </div>

            <button
              onClick={calculateQPCREfficiency}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate qPCR Efficiency
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">qPCR Amplification Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Amplification Efficiency</h3>
                    <p className="text-gray-600">{results.efficiency} (Efficiency: {results.efficiencyPercentage}%)</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Statistical Analysis</h3>
                    <p className="text-gray-600">R²: {results.rSquared}</p>
                    <p className="text-gray-600">Standard Deviation: {results.stdDev}</p>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Line
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'CT Values'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Cycle Number'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'CT Values vs Cycle Number',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Analysis Summary</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Efficiency:</span>
                      {results.efficiencyPercentage}%
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">R² Value:</span>
                      {results.rSquared}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium text-gray-600 mr-2">Standard Deviation:</span>
                      {results.stdDev}
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding qPCR Efficiency <span className="text-blue-600">📊</span></h2>
            <div className="text-gray-600 mb-8">
              <p>qPCR (quantitative PCR) efficiency is a crucial metric in molecular biology that helps in:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Gene Expression Analysis: Accurate measurement of target gene levels</li>
                <li>Pathogen Detection: Reliable quantification of pathogen load</li>
                <li>Diagnostic Testing: Consistent and reproducible results</li>
                <li>Research Applications: Validating experimental results</li>
              </ul>
              <p className="italic mt-2">Note: Ideal qPCR efficiency is between 90-110% (slope of -3.3 to -3.6)</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use qPCR Efficiency Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the qPCR Efficiency Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Prepare Your Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Collect cycle numbers and CT values from your qPCR run</li>
                  <li>Measure template concentrations for each sample</li>
                  <li>Enter data in comma-separated format</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Enter Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input cycle numbers in the first field</li>
                  <li>Enter corresponding CT values in the second field</li>
                  <li>Add template concentrations in the third field</li>
                  <li>Optionally enter slope and intercept values</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Analyze Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review amplification efficiency percentage</li>
                  <li>Check R² value for data correlation</li>
                  <li>Compare actual vs theoretical CT values</li>
                  <li>Assess standard deviation for consistency</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Efficiency should be between 90-110% for reliable results</li>
                  <li>R² value should be close to 1 for good correlation</li>
                  <li>Standard deviation should be low for consistent amplification</li>
                  <li>Check for outliers in your data points</li>
                  <li>Consider optimizing PCR conditions if efficiency is low</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QPCREfficiencyCalculator;